import { useState, useRef } from 'react';
import { api } from '@/lib/api';
import { Upload, Link as LinkIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Image input that supports both URL paste and file upload.
 * Posts file to /api/admin/upload (admin only) and calls onChange with the returned URL.
 */
export function ImageInput({ value, onChange, label = 'Image URL', testid = 'image-input' }) {
    const [mode, setMode] = useState('url'); // 'url' | 'upload'
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const onPick = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!/^image\//.test(f.type)) {
            toast.error('Please choose an image file');
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', f);
            const { data } = await api.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            // Convert /api/uploads/... to absolute via backend URL
            const absolute = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
            onChange(absolute);
            toast.success('Image uploaded');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Upload failed');
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest text-white/50">{label}</div>
                <div className="inline-flex rounded-md border border-white/10 overflow-hidden text-[10px]">
                    <button
                        type="button"
                        onClick={() => setMode('url')}
                        className={`px-2 py-1 inline-flex items-center gap-1 ${mode === 'url' ? 'bg-white/10 text-white' : 'text-white/50'}`}
                        data-testid={`${testid}-mode-url`}
                    >
                        <LinkIcon className="w-3 h-3" /> URL
                    </button>
                    <button
                        type="button"
                        onClick={() => setMode('upload')}
                        className={`px-2 py-1 inline-flex items-center gap-1 ${mode === 'upload' ? 'bg-white/10 text-white' : 'text-white/50'}`}
                        data-testid={`${testid}-mode-upload`}
                    >
                        <Upload className="w-3 h-3" /> Upload
                    </button>
                </div>
            </div>
            {mode === 'url' ? (
                <input
                    type="text"
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="https://…"
                    data-testid={`${testid}-url-input`}
                    className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm font-mono"
                />
            ) : (
                <div>
                    <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        data-testid={`${testid}-upload-button`}
                        className="w-full bg-white/5 border border-dashed border-white/15 hover:border-amber-400/40 rounded-md px-3 py-6 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        {uploading ? 'Uploading…' : 'Click to upload image (JPG / PNG / WEBP, max 10MB)'}
                    </button>
                    <input ref={fileRef} type="file" accept="image/*" onChange={onPick} className="hidden" data-testid={`${testid}-file-input`} />
                </div>
            )}
            {value ? (
                <div className="mt-2">
                    <img
                        src={value}
                        alt="preview"
                        className="w-32 h-24 rounded-md object-cover bg-black/40 border border-white/10"
                        onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                    />
                </div>
            ) : null}
        </div>
    );
}
